<template>
  <PageWrapper
    :maxWidth="5000"
    backIcon="arrow-right"
    :handleBack="goToListWashup"
    :title="$lang.competency_score"
  >
    <template #left-header>
      <Button
        variant="outline-complementary"
        rightIcon="printer-outline"
        :text="$lang.print"
        @click="handlePrint"
      />
    </template>
    <LoadingWrapper :loading="isLoading">
      <template #loading>
        <Loading :count="5" />
      </template>
      <template #default>
        <b-row class="margin-0">
          <b-col cols="12" class="padding-0">
            <div
              class="height--max-content overflow-x-auto background-surface margin-top-24 border--solid border--complementary-50 border-radius-8 padding-16"
              id="section-to-print"
            >
              <div
                class="min-width-1200 width--max-content table-grid-row font-size-12 color-complementary-300 margin-bottom-8 min-width-1200"
                :style="`gap: 8px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthLabels};`"
              >
                <div
                  v-for="(label, index) in labels"
                  :key="`a-${index}`"
                  class="height-64 flex-center padding-x-8 background-light-gray border-radius-top-8"
                  :class="
                    !index
                      ? 'background-light-gray'
                      : 'background-double-light-gray'
                  "
                  v-b-tooltip.hover
                  :title="label"
                >
                  <span
                    class="font-size-16 color-complementary-400 text-ellipsis--line-2 text-center"
                  >
                    {{ label }}
                  </span>
                </div>
              </div>
              <div class="min-width-1200 width--max-content">
                <div
                  v-for="({ candidate, items }, index) in values"
                  :key="`b-${index}`"
                  class="table-grid-row margin-bottom-8 last-child-border-radius-left-4 border--solid border--complementary-50 border-radius-8 padding-16 width-percent-100"
                  :style="`gap: 20px; grid-template-columns: repeat(${labels.length}, 1fr); grid-template-columns: ${generateColumnWidthValues};`"
                >
                  <div
                    class="font-size-14 color-complementary-300 border--solid border--complementary-50 border--left border--width-2"
                  >
                    <AvatarGroup
                      :data="candidate"
                      :hasType="false"
                      :hasProfilePopup="false"
                    />
                  </div>
                  <div
                    v-for="({ score }, itemIndex) in items"
                    :key="`case-${itemIndex}`"
                    class="font-size-14 color-complementary-300 text-center flex-center"
                  >
                    <span class="color">
                      {{ score }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
    </LoadingWrapper>
  </PageWrapper>
</template>

<script>
import {
  PageWrapper,
  AvatarGroup,
  LoadingWrapper,
} from "@/components/re-design";
import { Button } from "@/components/re-design/forms";
import Loading from "./loading/Loading.vue";
import { computed, ref } from "vue";
import { pathOr } from "ramda";
import {
  generateCurrentRoute,
  generateError,
  generateLang,
  goToRoute,
  setUniqueArrayOfObjects,
} from "@/shared/utils";
import { getCompetencyOverview } from "@/modules/kanoon/services";
export default {
  components: { PageWrapper, AvatarGroup, LoadingWrapper, Loading, Button },
  setup() {
    const generateLabels = (labels) => {
      const centerCompetencies = labels
        .map((item) => pathOr([], ["items"], item))
        .flat()
        .map((item) => pathOr(null, ["competency"], item));

      const uniqueItems = setUniqueArrayOfObjects(centerCompetencies, "id");
      const candidates = generateLang(["candidates"]);
      return [candidates, ...uniqueItems.map(({ caption }) => caption)];
    };
    const generateCandidateData = (item) => {
      const candidate = pathOr(null, ["candidate"], item);
      const candidateName =
        pathOr("", ["person", "full_name"], candidate) || "**********";
      const candidateNumber = pathOr(
        "",
        ["person_center_execute_code"],
        candidate
      );
      return {
        type: generateLang(["candidate"]),
        id: candidateNumber,
        name: candidateName,
        images: [pathOr("", ["person", "img_thumb"], candidate)],
      };
    };
    const generateScores = (item) => {
      const items = pathOr([], ["items"], item);
      const rows = items.map((row) => {
        return {
          score: pathOr(null, ["score"], row),
          competency: pathOr(null, ["competency"], row),
        };
      });
      return rows;
    };
    const generateValues = (items) => {
      return items.map((item) => {
        const candidate = generateCandidateData(item);
        const items = generateScores(item);
        return { candidate, items };
      });
    };
    const centerExecuteId = generateCurrentRoute(["params", "centerExecuteId"]);
    const labels = ref([]);
    const values = ref([]);
    const isLoading = ref(false);
    const getCompetencyOverviewData = async () => {
      try {
        isLoading.value = true;
        const response = await getCompetencyOverview(centerExecuteId);
        const { items } = response;
        labels.value = generateLabels(items);
        values.value = generateValues(items);
      } catch (error) {
        generateError(error);
      } finally {
        isLoading.value = false;
      }
    };
    getCompetencyOverviewData();

    const generateColumnWidthLabels = computed(() =>
      labels.value.map((item, index) => (index ? "125px" : "200px")).join(" ")
    );
    const generateColumnWidthValues = computed(() =>
      labels.value.map((item, index) => (index ? "112px" : "185px")).join(" ")
    );

    const goToListWashup = () => {
      const name = "list-washup";
      goToRoute(name, { centerExecuteId }, {}, false);
    };

    const handlePrint = () => {
      window.print();
    };

    return {
      labels,
      values,
      generateColumnWidthLabels,
      generateColumnWidthValues,
      goToListWashup,
      handlePrint,
      isLoading,
    };
  },
};
</script>
<style lang="scss">
.table-grid-row {
  display: grid;
  grid-template-rows: auto;
  align-items: start;
}

.border-radius-left-4 {
  border-radius: 4px 0 0 4px;
}
.last-child-border-radius-left-4 {
  span:last-child {
    @extend .border-radius-left-4;
  }
}
</style>
